/**
 * Nova's suggestion model — the output of her intelligence layer.
 *
 * Each rule in /lib/novaRules.ts returns zero or more `NovaSuggestion`
 * objects. The engine ranks them by priority + recency and surfaces
 * the top one in NovaCard.
 *
 * Keep this shape stable — both web and iOS consume it. iOS will
 * mirror the same field names in Swift.
 */

export type NovaSuggestionKind =
  // Inquiry funnel
  | 'idle_inquiry'         // pending lead, no contact in N days
  | 'fresh_lead'           // brand-new inquiry just landed
  | 'no_inquiries'         // no leads in a while → marketing nudge
  // Production
  | 'upcoming_shoot'       // shoot in next 1–3 days
  | 'stalled_project'      // editing too long, deadline drift
  | 'quiet_week'           // no shoots scheduled this week
  // Delivery + revenue
  | 'fresh_delivery'       // upload sitting unreviewed by client
  | 'delivery_followup'    // delivered N+ days ago, time to upsell or check in
  | 'outstanding_invoice'  // sent + unpaid for 14+ days
  // Onboarding nudges
  | 'no_clients_yet'       // empty studio, gentle prompt
  | 'no_first_project';    // has clients but never created a project

/**
 * Priority bands. Higher = more attention. The engine sorts by
 * priority then recency, so a "fresh lead" outranks a "quiet week"
 * even if both fire at the same moment.
 */
export type NovaSuggestionPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface NovaSuggestionAction {
  /** Human label, e.g. "Draft follow-up", "Open Cypress" */
  label: string;
  /** Where to navigate — relative path on web, deep link on iOS. */
  href?: string;
  /** Optional second action label (the "Not now" / "Snooze" path). */
  dismissLabel?: string;
}

export interface NovaSuggestion {
  /** Stable id so the UI can remember "user dismissed this one". */
  id: string;
  kind: NovaSuggestionKind;
  /** One-line headline. */
  title: string;
  /** Supporting detail — keep under ~140 chars. */
  body: string;
  priority: NovaSuggestionPriority;
  /** ISO timestamp of when the underlying signal happened. */
  observedAt: string;
  action?: NovaSuggestionAction;
  /** Optional context — used by the UI to deep-link. */
  projectId?: string;
  clientId?: string;
  inquiryId?: string;
  /** Which Nova emotion to show with this suggestion. */
  novaState?: 'has-suggestion' | 'happy' | 'curious' | 'celebrating' | 'sad' | 'cross';
}

/** Numeric weight used for sorting. */
export const PRIORITY_WEIGHT: Record<NovaSuggestionPriority, number> = {
  urgent: 4,
  high: 3,
  medium: 2,
  low: 1,
};
